import {
  useBanksDeleteUser,
  useBanksListUsers,
} from '@b7hio/api-lib/src/gen/bank-portal-api';
import {
  PortalUserCreateRequest,
  PortalUserResponse,
  PortalUserUpdateRequest,
  useCreateUser,
  useDeleteUser,
  useListUsers,
  useUpdateUser,
} from '@b7hio/api-lib/src/ops-portal';
import { PortalType, useSnackbar } from '@b7hio/core-lib/src';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

export function useUserInteractivity(portal: PortalType) {
  const { t } = useTranslation(['common', 'shell', 'users']);
  const [state, setState] = useState<
    'list' | 'create' | 'update' | 'delete' | 'duplicate'
  >('list');
  const [target, setTarget] = useState<PortalUserResponse | undefined>();
  const { enqueueSnackbar } = useSnackbar();
  const { mutateAsync: createUser } = useCreateUser();
  const { mutateAsync: updateUser } = useUpdateUser();
  const { data, isLoading, refetch } =
    portal === 'ops' ? useListUsers() : useBanksListUsers();
  const { mutateAsync: deleteUser } =
    portal === 'ops' ? useDeleteUser() : useBanksDeleteUser();

  const handleClose = (event: object, reason: string) => {
    const closeCheck =
      reason !== 'skip' ? confirm(t('common:alerts.FORM_CLOSE_CHECK')) : true;

    if (closeCheck) {
      setState('list');
      setTarget(undefined);
    }
  };

  const handleRequestCreate = () => setState('create');
  const handleRequestUpdate = (user: PortalUserResponse) => {
    setTarget(user);
    setState('update');
  };
  const handleRequestDelete = (user: PortalUserResponse) => {
    setTarget(user);
    setState('delete');
  };
  const handleRequestDuplicate = (user: PortalUserResponse) => {
    setTarget(user);
    setState('duplicate');
  };

  const handleCreate = async (values: PortalUserCreateRequest) => {
    try {
      await createUser({ data: values });
      await refetch();
      setState('list');
      enqueueSnackbar(t('users:feedback.createUserSuccess'), 'success');
    } catch (error) {
      enqueueSnackbar(t('users:feedback.createUserFailed'), 'error');
      throw error;
    }
  };

  const handleUpdate = async (values: PortalUserUpdateRequest) => {
    if (!target) {
      return;
    }

    try {
      await updateUser({
        data: values,
        userUid: target.uid,
      });

      await refetch();
      setState('list');
      setTarget(undefined);
      enqueueSnackbar(t('users:feedback.updateUserSuccess'), 'success');
    } catch (error) {
      enqueueSnackbar(t('users:feedback.updateUserFailed'), 'error');
      throw error;
    }
  };

  const handleDelete = async (user: PortalUserResponse) => {
    try {
      await deleteUser({ userUid: user.uid });

      await refetch();
      setState('list');
      setTarget(undefined);
      enqueueSnackbar(t('users:feedback.deleteUserSuccess'), 'success');
    } catch (error) {
      enqueueSnackbar(t('users:feedback.deleteUserFailed'), 'error');
      throw error;
    }
  };

  return {
    state,
    target,
    data,
    isLoading,
    handleClose,
    handleRequestCreate,
    handleRequestUpdate,
    handleRequestDelete,
    handleRequestDuplicate,
    handleCreate,
    handleUpdate,
    handleDelete,
  };
}
